import { useState, useRef, useEffect, useMemo } from "react";

interface SearchableSelectProps<T> {
  options: T[] | undefined | null;
  value: string | number | undefined | null; // currently selected id
  onSelect: (option: T | null) => void;
  getId: (option: T) => string | number;
  getLabel: (option: T) => string;
  getSubLabel?: (option: T) => string; // optional secondary text, e.g. employee_id
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  className?: string; // NE// useful if you want it to behave like a form field
}

function SearchableSelect<T>({
  options,
  value,
  onSelect,
  getId,
  getLabel,
  getSubLabel,
  placeholder = "Type to search",
  disabled,
  required,
  name,
  className, // NEW
}: SearchableSelectProps<T>) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const list = useMemo(() => options ?? [], [options]);

  const displayText = (opt: T) =>
    getSubLabel ? `${getLabel(opt)} (${getSubLabel(opt)})` : getLabel(opt);

  // Sync input text when value changes externally (edit mode, reset, etc.)
  useEffect(() => {
    const selected = list.find((o) => String(getId(o)) === String(value));
    setQuery(selected ? displayText(selected) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, list]);

  // Close on outside click, revert to last valid selection
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        const selected = list.find((o) => String(getId(o)) === String(value));
        setQuery(selected ? displayText(selected) : "");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, list]);

  const filtered = useMemo(() => {
    if (query.trim() === "") return list;
    const q = query.toLowerCase();
    return list.filter((opt) => {
      const haystack = getSubLabel
        ? `${getLabel(opt)} ${getSubLabel(opt)}`
        : getLabel(opt);
      return haystack.toLowerCase().includes(q);
    });
  }, [query, list]);

  const handleSelect = (opt: T) => {
    onSelect(opt);
    setQuery(displayText(opt));
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filtered[highlightIndex]) {
        handleSelect(filtered[highlightIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <>
      <div className="position-relative" ref={wrapperRef}>
        <input
          className={`form-control ${className ?? ""}`}
          name={name}
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          required={required}
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setHighlightIndex(-1);
            if (e.target.value.trim() === "") onSelect(null);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {isOpen && filtered.length > 0 && (
          <ul
            className="list-group position-absolute w-100 shadow-sm"
            style={{ zIndex: 1000, maxHeight: "220px", overflowY: "auto" }}
          >
            {filtered.map((opt, idx) => (
              <li
                key={getId(opt)}
                className={`list-group-item list-group-item-action ${
                  idx === highlightIndex ? "active" : ""
                }`}
                style={{ cursor: "pointer" }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(opt);
                }}
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                {displayText(opt)}
              </li>
            ))}
          </ul>
        )}
        {isOpen && filtered.length === 0 && (
          <ul
            className="list-group position-absolute w-100 shadow-sm"
            style={{ zIndex: 1000 }}
          >
            <li className="list-group-item text-muted">No matches found</li>
          </ul>
        )}
      </div>
      <style>
        {`
        .field-error {
          color: #dc3545;
          font-size: 0.8125rem;
          margin-top: 4px;
          animation: fieldErrorIn 0.2s ease-out;
        }
        @keyframes fieldErrorIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .form-control.is-invalid,
        .form-select.is-invalid {
          border-color: #dc3545 !important;
          transition: border-color 0.15s ease-in-out;
        }
      `}
      </style>
    </>
  );
}

export default SearchableSelect;
